// src/lib/searchParser.js
// Advanced search parser supporting operators, wildcards, and phrases

/**
 * Parse a search query into structured terms
 * Supports:
 * - AND/OR operators (case insensitive)
 * - Quoted phrases: "exact phrase"
 * - Wildcards: word* or *word or *word*
 * - Negation: -word or -"phrase"
 * - Parentheses for grouping: (word1 OR word2) AND word3
 */
export function parseSearchQuery(query) {
  if (!query || typeof query !== 'string') {
    return { terms: [], isSimple: true };
  }

  const originalQuery = query.trim();
  if (!originalQuery) {
    return { terms: [], isSimple: true };
  }

  // Check if this is a simple search (no operators, quotes, or special characters)
  const hasOperators = /\b(AND|OR)\b/i.test(originalQuery);
  const hasQuotes = originalQuery.includes('"');
  const hasWildcards = originalQuery.includes('*');
  const hasNegation = /\s-\w/.test(originalQuery) || originalQuery.startsWith('-');
  const hasParentheses = originalQuery.includes('(') || originalQuery.includes(')');

  const isSimple = !hasOperators && !hasQuotes && !hasWildcards && !hasNegation && !hasParentheses;

  if (isSimple) {
    // Simple search - just split on whitespace
    const words = originalQuery.toLowerCase().split(/\s+/).filter(word => word.length > 0);
    return {
      terms: words.map(word => ({ type: 'word', value: word, operator: 'AND' })),
      isSimple: true
    };
  }

  // Complex parsing for advanced search
  try {
    const tokens = tokenize(originalQuery);
    const parsedTerms = parseTokens(tokens);
    return {
      terms: parsedTerms,
      isSimple: false
    };
  } catch (error) {
    console.warn('Search parsing error, falling back to simple search:', error);
    // Fallback to simple search
    const words = originalQuery.toLowerCase().split(/\s+/).filter(word => word.length > 0);
    return {
      terms: words.map(word => ({ type: 'word', value: word, operator: 'AND' })),
      isSimple: true
    };
  }
}

/**
 * Tokenize the search query into components
 */
function tokenize(query) {
  const tokens = [];
  let i = 0;
  
  while (i < query.length) {
    const char = query[i];
    
    // Skip whitespace
    if (/\s/.test(char)) {
      i++;
      continue;
    }
    
    // Handle quoted phrases
    if (char === '"') {
      const phrase = extractQuotedPhrase(query, i);
      tokens.push({ type: 'phrase', value: phrase.content, negated: false });
      i = phrase.endIndex + 1;
      continue;
    }
    
    // Handle negated terms
    if (char === '-') {
      if (i + 1 < query.length) {
        if (query[i + 1] === '"') {
          // Negated phrase
          const phrase = extractQuotedPhrase(query, i + 1);
          tokens.push({ type: 'phrase', value: phrase.content, negated: true });
          i = phrase.endIndex + 1;
        } else {
          // Negated word
          const word = extractWord(query, i + 1);
          tokens.push({ type: 'word', value: word.content.toLowerCase(), negated: true });
          i = word.endIndex;
        }
        continue;
      }
    }
    
    // Handle parentheses
    if (char === '(') {
      tokens.push({ type: 'lparen' });
      i++;
      continue;
    }
    
    if (char === ')') {
      tokens.push({ type: 'rparen' });
      i++;
      continue;
    }
    
    // Handle words (including potential operators)
    const word = extractWord(query, i);
    const wordUpper = word.content.toUpperCase();
    
    if (wordUpper === 'AND') {
      tokens.push({ type: 'operator', value: 'AND' });
    } else if (wordUpper === 'OR') {
      tokens.push({ type: 'operator', value: 'OR' });
    } else {
      tokens.push({ type: 'word', value: word.content.toLowerCase(), negated: false });
    }
    
    i = word.endIndex;
  }
  
  return tokens;
}

/**
 * Extract a quoted phrase from the query
 */
function extractQuotedPhrase(query, startIndex) {
  let i = startIndex + 1; // Skip opening quote
  let content = '';
  
  while (i < query.length && query[i] !== '"') {
    content += query[i];
    i++;
  }
  
  return {
    content: content.trim(),
    endIndex: i // Points to closing quote or end of string
  };
}

/**
 * Extract a word from the query
 */
function extractWord(query, startIndex) {
  let i = startIndex;
  let content = '';
  
  while (i < query.length && !/\s/.test(query[i]) && query[i] !== '(' && query[i] !== ')' && query[i] !== '"') {
    content += query[i];
    i++;
  }
  
  return {
    content: content,
    endIndex: i
  };
}

/**
 * Parse tokens into structured search terms
 */
function parseTokens(tokens) {
  const terms = [];
  let currentOperator = 'AND';
  
  for (let i = 0; i < tokens.length; i++) {
    const token = tokens[i];
    
    if (token.type === 'operator') {
      currentOperator = token.value;
    } else if (token.type === 'word' || token.type === 'phrase') {
      terms.push({
        type: token.type,
        value: token.value,
        operator: currentOperator,
        negated: token.negated || false
      });
      // Reset to AND for next term unless explicitly specified
      if (i + 1 < tokens.length && tokens[i + 1].type !== 'operator') {
        currentOperator = 'AND';
      }
    }
    // Skip parentheses for now - could be implemented for grouping
  }
  
  return terms;
}

/**
 * Test if a text matches the parsed search terms
 */
export function matchesSearchTerms(text, searchTerms) {
  if (!text || !searchTerms || searchTerms.length === 0) {
    return searchTerms.length === 0;
  }
  
  const textLower = String(text).toLowerCase();
  
  // For simple searches, all terms must match
  if (searchTerms.isSimple) {
    return searchTerms.terms.every(term => matchesTerm(textLower, term));
  }
  
  // For complex searches, evaluate the expression
  return evaluateSearchExpression(textLower, searchTerms.terms);
}

/**
 * Check if text matches a single search term
 */
function matchesTerm(text, term) {
  const { type, value, negated } = term;
  let matches = false;
  
  if (type === 'phrase') {
    matches = text.includes(value.toLowerCase());
  } else if (type === 'word') {
    if (value.includes('*')) {
      // Handle wildcards
      matches = matchesWildcard(text, value);
    } else {
      // Exact word match (but within the text)
      matches = text.includes(value);
    }
  }
  
  return negated ? !matches : matches;
}

/**
 * Match text against wildcard pattern
 */
function matchesWildcard(text, pattern) {
  // Convert wildcard pattern to regex
  const escapedPattern = pattern.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const regexPattern = escapedPattern.replace(/\\\*/g, '.*');
  const regex = new RegExp(regexPattern, 'i');
  return regex.test(text);
}

/**
 * Evaluate a complex search expression with AND/OR logic
 */
function evaluateSearchExpression(text, terms) {
  if (terms.length === 0) return true;
  if (terms.length === 1) return matchesTerm(text, terms[0]);
  
  // Group terms by operator
  const groups = [];
  let currentGroup = { operator: 'AND', terms: [] };
  
  for (const term of terms) {
    if (currentGroup.terms.length === 0 || term.operator === currentGroup.operator) {
      currentGroup.terms.push(term);
    } else {
      // New operator, start new group
      groups.push(currentGroup);
      currentGroup = { operator: term.operator, terms: [term] };
    }
  }
  
  if (currentGroup.terms.length > 0) {
    groups.push(currentGroup);
  }
  
  // Evaluate each group
  const groupResults = groups.map(group => {
    if (group.operator === 'AND') {
      return group.terms.every(term => matchesTerm(text, term));
    } else { // OR
      return group.terms.some(term => matchesTerm(text, term));
    }
  });
  
  // Combine group results (OR between groups by default)
  return groupResults.some(result => result);
}

/**
 * Generate help text for search operators
 */
export function getSearchHelpText() {
  return [
    'Search supports advanced operators:',
    '• Use quotes for exact phrases: "police shooting"',
    '• Use * for wildcards: police* matches police, policeman, etc.',
    '• Use AND/OR for logic: police AND shooting OR "excessive force"',
    '• Use - to exclude: police -lawsuit',
    '• Multiple terms default to AND'
  ];
}